import { formatDate, formatMessage, formatNumber } from "devextreme/localization";
import { BankingDayConvention, RateConvention, RateFloor, SupportedLanguage } from "./Models";

export function changeLocale(locale: SupportedLanguage) {
    document.location.hash = `/${locale}/`;

    if (document.location.pathname.endsWith("/")) {
        document.location.reload();
    } else {
        document.location.pathname = document.location.pathname + "/";
    }
}

export function getLocale(): SupportedLanguage {
    const localeFromHash = window.location.hash.substring(2, 4);
    const selectedLocale = localeFromHash !== "" ? localeFromHash : navigator.language;

    switch (selectedLocale) {
        case "nb":
        case "en":
            return selectedLocale;
        case "no":
            return "nb";
        default:
            return "en";
    }
}

export interface ITranslations {
    "title": string;
    "header.linkToNorgesBankText": string;

    "intro.title": string;

    "nowaForm.title": string;
    "nowaForm.startDate": string;
    "nowaForm.startDate.hint": string;
    "nowaForm.endDate": string;
    "nowaForm.endDate.hint": string;
    "nowaForm.lagLabelObservationShift": string;
    "nowaForm.lagLabelLookback": string;
    "nowaForm.lagLabelLockout": string;
    "nowaForm.lagLabelDelayedPayment": string;
    "nowaForm.principal": string;
    "nowaForm.principal.hint": string;
    "nowaForm.spread": string;
    "nowaForm.spread.hint": string;
    "nowaForm.dayCountConvention": string;
    "nowaForm.dayCountConvention.hint": string;
    "nowaForm.bankingDayConvention": string;
    "nowaForm.bankingDayConvention.hint": string;
    "nowaForm.rateFloor": string;
    "nowaForm.rateFloor.hint": string;
    "nowaForm.strike": string;
    "nowaForm.strike.hint": string;
    "nowaForm.rateConvention": string;
    "nowaForm.rateConvention.hint": string;
    "nowaForm.rounding": string;
    "nowaForm.rounding.hint": string;
    "nowaForm.reset": string;
    "nowaForm.advancedOptions": string;

    "table.rate": string;
    "table.appliedRate": string;
    "table.principal": string;
    "table.factor": string;
    "table.compoundingFactor": string;
    "table.observationDate": string;
    "table.rateDate": string;
    "table.calendarDayCount": string;
    "table.accruedInterest": string;
    "table.accruedInterest.hint": string;
    "table.annualRate": string;
    "table.annualRate.hint": string;
    "table.totalRate": string;
    "table.totalRate.hint": string;
    "table.daysObservationPeriod": string;
    "table.daysObservationPeriod.hint": string;
    "table.daysRatePeriod": string;
    "table.daysRatePeriod.hint": string;
    "table.effectiveRatePeriodStart": string;
    "table.effectiveRatePeriodStart.hint": string;
    "table.effectiveRatePeriodEnd": string;
    "table.effectiveRatePeriodEnd.hint": string;
    "table.settlementDate": string;
    "table.settlementDate.hint": string;
    "table.adjustedWithBankDayConvention": string;
    "table.title": string;
    "table.export": string;

    "enums.rateFloor.none": string;
    "enums.rateFloor.daily": string;
    "enums.rateFloor.annual": string;
    "enums.rateConvention.observationShift": string;
    "enums.rateConvention.lookback": string;
    "enums.rateConvention.lockout": string;
    "enums.rateConvention.delayedPayment": string;
    "enums.bankingDayConvention.modifiedFollowing": string;
    "enums.bankingDayConvention.preceding": string;
}


const dictionary: { [key: string]: ITranslations } = {
    nb: {
        "title": "Nowa-kalkulator",

        "header.linkToNorgesBankText": "Til Norges Banks nettside",

        "intro.title": "Nowa-kalkulator",

        "nowaForm.title": "Parametere",
        "nowaForm.startDate": "Startdato",
        "nowaForm.startDate.hint": "Første dag i renteperioden",
        "nowaForm.endDate": "Sluttdato",
        "nowaForm.endDate.hint": "Siste dag i renteperioden",
        "nowaForm.lagLabelObservationShift": "Antall dager skift",
        "nowaForm.lagLabelLookback": "Antall dager lookback",
        "nowaForm.lagLabelLockout": "Antall dager lockout",
        "nowaForm.lagLabelDelayedPayment": "Antall dager utsatt betaling",
        "nowaForm.principal": "Hovedstol (NOK)",
        "nowaForm.principal.hint": "Beløp som inngår i beregning av påløpte renter",
        "nowaForm.spread": "Påslag (prosentenheter)",
        "nowaForm.spread.hint": "Påslag som legges på annualisert rente, kapitaliseres ikke",
        "nowaForm.dayCountConvention": "Dagsbasis",
        "nowaForm.dayCountConvention.hint": "Antall dager som inngår i beregning av annualisert rente",
        "nowaForm.bankingDayConvention": "Justering ved helligdag",
        "nowaForm.bankingDayConvention.hint": "Metode for datojustering ved helligdag",
        "nowaForm.rateFloor": "Rentegulv",
        "nowaForm.rateFloor.hint": "Valg av rentegulv",
        "nowaForm.strike": "Minimumsrente (%)",
        "nowaForm.strike.hint": "Valg av minimumsrente ved rentegulv",
        "nowaForm.rateConvention": "Rentekonvensjon",
        "nowaForm.rateConvention.hint": "Metode for beregning av kapitalisert rente",
        "nowaForm.rounding": "Avrunding",
        "nowaForm.rounding.hint": "Antall desimaler annualisert rente",
        "nowaForm.reset": "Nullstill",
        "nowaForm.advancedOptions": "Avanserte innstillinger",

        "table.rate": "Nowa",
        "table.appliedRate": "Rente brukt",
        "table.principal": "Akkumulert sum",
        "table.factor": "Faktor",
        "table.compoundingFactor": "Kapitaliseringsfaktor",
        "table.observationDate": "Observasjonsperiode",
        "table.rateDate": "Renteperiode",
        "table.calendarDayCount": "Antall dager",
        "table.accruedInterest": "Påløpte renter",
        "table.accruedInterest.hint": "Rentebeløp basert på total rente (annualisert kapitalisert rente + eventuelt påslag)",

        "table.annualRate": "Annualisert rente",
        "table.annualRate.hint": "Annualisert kapitalisert rente",
        "table.totalRate": "Total rente",
        "table.totalRate.hint": "Annualisert kapitalisert rente + eventuelt påslag",
        "table.daysObservationPeriod": "Dager observasjonsperiode",
        "table.daysObservationPeriod.hint": "Antall dager som inngår i beregningen av kapitalisert rente",
        "table.daysRatePeriod": "Dager renteperiode",
        "table.daysRatePeriod.hint": "Antall dager angitt av start- og sluttdato",
        "table.effectiveRatePeriodStart": "Renteperiode start",
        "table.effectiveRatePeriodStart.hint": "Renteperiodens start (inkl. justering helligdag)",
        "table.effectiveRatePeriodEnd": "Renteperiode slutt",
        "table.effectiveRatePeriodEnd.hint": "Renteperiodens slutt (inkl. justering helligdag)",
        "table.settlementDate": "Oppgjørsdato",
        "table.settlementDate.hint": "Dato for oppgjør. Justeres kun ved utsatt betaling",
        "table.adjustedWithBankDayConvention": "Justert med",
        "table.title": "Resultat",
        "table.export": "Eksporter",

        "enums.rateFloor.none": "Ingen",
        "enums.rateFloor.daily": "Daglig",
        "enums.rateFloor.annual": "Annualisert",
        "enums.rateConvention.observationShift": "Skiftet observasjonsperiode",
        "enums.rateConvention.lookback": "Lookback",
        "enums.rateConvention.lockout": "Lockout",
        "enums.rateConvention.delayedPayment": "Utsatt betaling",
        "enums.bankingDayConvention.modifiedFollowing": "Modifisert påfølgende bankdag",
        "enums.bankingDayConvention.preceding": "Foregående bankdag"
    },
    en: {
        "title": "Nowa-calculator",

        "header.linkToNorgesBankText": "To Norges Bank's website",

        "intro.title": "Nowa-calculator",

        "nowaForm.title": "Parameters",
        "nowaForm.startDate": "Start date",
        "nowaForm.startDate.hint": "First day of interest period",
        "nowaForm.endDate": "End date",
        "nowaForm.endDate.hint": "Last day of interest period",
        "nowaForm.lagLabelObservationShift": "Number of days shift",
        "nowaForm.lagLabelLookback": "Number of days lookback",
        "nowaForm.lagLabelLockout": "Lockout days",
        "nowaForm.lagLabelDelayedPayment": "Number of days delayed payment",
        "nowaForm.principal": "Principal (NOK)",
        "nowaForm.principal.hint": "Loan amount included in the calculation of accrued interest",
        "nowaForm.spread": "Spread (percentage points)",
        "nowaForm.spread.hint": "Spread added to annual rate. Spread is not compounded daily",
        "nowaForm.dayCountConvention": "Day count convention",
        "nowaForm.dayCountConvention.hint": "Selected convention for determining the number of days in a year",
        "nowaForm.bankingDayConvention": "Date rolling",
        "nowaForm.bankingDayConvention.hint": "Method for date rolling",
        "nowaForm.rateFloor": "Rate floor",
        "nowaForm.rateFloor.hint": "Selection of rate floor",
        "nowaForm.strike": "Minimum interest rate (%)",
        "nowaForm.strike.hint": "Minimum interest rate",
        "nowaForm.rateConvention": "Interest convention",
        "nowaForm.rateConvention.hint": "Interest convention for calculating annual rate",
        "nowaForm.rounding": "Rounding",
        "nowaForm.rounding.hint": "Number of decimals in annual rate",
        "nowaForm.reset": "Reset",
        "nowaForm.advancedOptions": "Advanced options",

        "table.rate": "Nowa",
        "table.appliedRate": "Applied rate",
        "table.principal": "Accumulated sum",
        "table.factor": "Factor",
        "table.compoundingFactor": "Compounding Factor",
        "table.observationDate": "Observation date",
        "table.rateDate": "Rate date",
        "table.calendarDayCount": "Number of days",
        "table.accruedInterest": "Accrued interest",
        "table.accruedInterest.hint": "Accrued interest based on total interest rate (annual rate + optional spread)",
        "table.annualRate": "Annual rate",
        "table.annualRate.hint": "Annual rate",
        "table.totalRate": "Total interest rate",
        "table.totalRate.hint": "Annual rate + optional spread",
        "table.daysObservationPeriod": "Days observation period",
        "table.daysObservationPeriod.hint": "Number of days included in calculation of annual rate",
        "table.daysRatePeriod": "Days interest period",
        "table.daysRatePeriod.hint": "Number of days between start and end date",
        "table.effectiveRatePeriodStart": "Interest period start",
        "table.effectiveRatePeriodStart.hint": "Interest period start (incl. date rolling)",
        "table.effectiveRatePeriodEnd": "Interest period end",
        "table.effectiveRatePeriodEnd.hint": "Interest period end (incl. date rolling)",
        "table.settlementDate": "Settlement date",
        "table.settlementDate.hint": "Settlement date. Only adjusted for delayed payment",
        "table.adjustedWithBankDayConvention": "Adjusted with",
        "table.title": "Result",
        "table.export": "Export",

        "enums.rateFloor.none": "None",
        "enums.rateFloor.daily": "Daily",
        "enums.rateFloor.annual": "Annual",
        "enums.rateConvention.observationShift": "Observation shift",
        "enums.rateConvention.lookback": "Lookback",
        "enums.rateConvention.lockout": "Lockout",
        "enums.rateConvention.delayedPayment": "Delayed payment",
        "enums.bankingDayConvention.modifiedFollowing": "Modified following bank day",
        "enums.bankingDayConvention.preceding": "Preceding bank day"
    },
}

export const translate = (key: keyof ITranslations) => {
    const translation = formatMessage(key);
    return translation !== "" ? translation : key;
};

export const getRateFloorDropdown = (_: SupportedLanguage): Array<{ name: string; value: RateFloor }> => {
    return [
        { name: translate("enums.rateFloor.none"), value: "None" },
        { name: translate("enums.rateFloor.daily"), value: "Daily" },
        { name: translate("enums.rateFloor.annual"), value: "Annual" }
    ]
}

export const getRateConventionDropdown = (_: SupportedLanguage): Array<{ name: string; value: RateConvention }> => {
    return [
        { name: translate("enums.rateConvention.observationShift"), value: "ObservationShift" },
        { name: translate("enums.rateConvention.lookback"), value: "Lookback" },
        { name: translate("enums.rateConvention.delayedPayment"), value: "DelayedPayment" },
        { name: translate("enums.rateConvention.lockout"), value: "Lockout" }
    ]
}

export const getBankingDayConventionDropdown = (_: SupportedLanguage): Array<{ name: string; value: BankingDayConvention }> => {
    return [
        { name: translate("enums.bankingDayConvention.modifiedFollowing"), value: "ModifiedFollowing" },
        { name: translate("enums.bankingDayConvention.preceding"), value: "Preceding" }
    ]
}

export function formatStringDate(date: string) {
    const parsed = new Date(date);
    return formatDate(parsed, { year: 'numeric', month: '2-digit', day: '2-digit' } as any);
}

export function formatPercent(input: number, rounding = 2) {
    return formatNumber(input, { type: "percent", precision: rounding })
}

export function formatFixedPoint(input: number) {
    return formatNumber(input, { type: "fixedPoint", precision: 2 });
}

function toFlatPropertyMap(obj: object, keySeparator = '.') {
    const flattenRecursive = (obj: object, parentProperty?: string, propertyMap: Record<string, unknown> = {}) => {
        for (const [key, value] of Object.entries(obj)) {
            const property = parentProperty ? `${parentProperty}${keySeparator}${key}` : key;
            if (value && typeof value === 'object') {
                flattenRecursive(value, property, propertyMap);
            } else {
                propertyMap[property] = value;
            }
        }
        return propertyMap;
    };
    return flattenRecursive(obj);
}

export const languageDictionaries = Object.keys(dictionary).reduce((acc: { [key: string]: any }, key: string) => {
    acc[key] = toFlatPropertyMap(dictionary[key]);
    return acc;
}, {});

(window as any).__I18N_UTILS__ = {
    formatStringDate,
    formatPercent,
    formatFixedPoint,
};
